import { AnalyticsBrowser } from "@customerio/cdp-analytics-browser";
import { Config } from "services/configuration/eatSmartConfig";
import { log } from "utils/logger";

/**
 * The whole Customer.io leg of analytics: one SDK instance and one `.load()` for the lifetime
 * of this page realm (JID-2139).
 *
 * The instance used to live in a per-mount `useRef` inside the analytics provider, so every
 * provider remount — React StrictMode's double-invocation in dev, an error-boundary reset in
 * production — built a second `AnalyticsBrowser` and fired a second `.load()`, registering a
 * second instance with Customer.io and duplicating the SDK/settings request. Module scope is
 * the only scope that survives a remount, so instance lifetime lives here instead.
 *
 * Everything is lazy: importing this module constructs nothing and loads nothing, so the SDK
 * is only touched once the app actually uses analytics. Both the construction and the load
 * are one-shot, and the flags are raised *before* the call they guard — a constructor that
 * throws or a load that rejects must degrade to "no Customer.io for this page" rather than be
 * retried on every render or every event.
 *
 * Analytics is best-effort: no failure here ever reaches a caller, and the write key is read
 * at call time (never at import time), so a kiosk without a key simply gets no Customer.io.
 */

const CUSTOMER_IO_CDN_URL = "https://cdp-eu.customer.io";

let instance: AnalyticsBrowser | null = null;
let constructionAttempted = false;
let loadStarted = false;

/**
 * The single `AnalyticsBrowser` of this page realm, constructed on first use.
 *
 * @return The instance, or `null` when construction failed (now or earlier).
 */
export const getCustomerIoInstance = (): AnalyticsBrowser | null => {
	if (constructionAttempted) {
		return instance;
	}
	// Raised before the `new`: a throwing constructor must be remembered, not retried on the
	// next call (which would re-log the same failure for every event, forever).
	constructionAttempted = true;

	try {
		instance = new AnalyticsBrowser();
	} catch (e) {
		// Degrade to no Customer.io rather than throwing into the caller — this runs from a
		// provider effect, where a throw would escape past the error boundary.
		log("Error while constructing Customer.io browser", "error", e);
	}

	return instance;
};

/**
 * Runs one Customer.io SDK call, best-effort: gated on the write key, skipped without an
 * instance, and with both synchronous throws and rejected thenables swallowed into a log.
 * `onFailure` runs before the log on either failure path.
 */
const withCustomerIo = (
	errorMessage: string,
	call: (customerIo: AnalyticsBrowser) => unknown,
	onFailure?: () => void,
): void => {
	// Read at call time, never at import time: the key is configuration, and module-level
	// capture would freeze whatever value existed when this module happened to be imported.
	if (!Config.tools.customerIoWriteKey) {
		return;
	}

	const customerIo = getCustomerIoInstance();
	if (!customerIo) {
		return;
	}

	try {
		// The SDK's buffered methods return thenables (`reset` returns nothing pre-load), so
		// attach the catch defensively and synchronously — an unattached rejection would
		// surface as an unhandled promise rejection in Sentry.
		(call(customerIo) as Promise<unknown> | undefined)?.catch?.((e: unknown) => {
			onFailure?.();
			log(errorMessage, "error", e);
		});
	} catch (e) {
		onFailure?.();
		log(errorMessage, "error", e);
	}
};

/** After a failed load every verb takes the no-instance path above; the memo keeps it that way. */
const dropInstance = (): void => {
	instance = null;
};

/**
 * Initializes the SDK at most once per page realm. A no-op without a write key, without an
 * instance, or after the first call. A failed load (throw or rejection) drops the instance: the
 * SDK's buffered wrapper only sets its inner instance and drains its queue once the load
 * resolves, so every later verb would otherwise queue for the life of the page.
 */
export const loadCustomerIoOnce = (): void => {
	if (loadStarted) {
		return;
	}

	const writeKey = Config.tools.customerIoWriteKey;
	if (!writeKey) {
		return;
	}

	withCustomerIo(
		"Error while initializing Customer.io",
		(customerIo) => {
			// Raised before `.load()`, inside the helper so it only counts as started once the
			// call is really about to happen (a failed construction stays retryable in principle,
			// though the construction memo already makes that a no-op).
			loadStarted = true;

			return customerIo.load({
				cdnURL: CUSTOMER_IO_CDN_URL,
				writeKey: writeKey,
			});
		},
		dropInstance,
	);
};

export const trackCustomerIo = (
	name: string,
	properties: Record<string, unknown>,
	options?: { messageId?: string },
): void => {
	withCustomerIo("Error while sending the track event to customer.io", (customerIo) =>
		customerIo.track(name, properties, options),
	);
};

export const identifyCustomerIo = (userId: string | undefined, traits: Record<string, unknown>): void => {
	withCustomerIo("Error while identifying user in Customer.io", (customerIo) => customerIo.identify(userId, traits));
};

export const setCustomerIoAnonymousId = (anonymousId: string): void => {
	withCustomerIo("Error while setting the Customer.io anonymous id", (customerIo) =>
		customerIo.setAnonymousId(anonymousId),
	);
};

export const resetCustomerIo = (): void => {
	withCustomerIo("Error while resetting Customer.io", (customerIo) => customerIo.reset());
};

/** Test-only: drops the memoized instance, the construction-failure memo and the load flag. */
export const resetCustomerIoClientForTests = (): void => {
	instance = null;
	constructionAttempted = false;
	loadStarted = false;
};
